import React, { useState, useEffect } from 'react';
import moment from 'moment';
import type { TTimesheet } from '../../../types/timesheet';
import RejectionModal from './RejectionModal';
import ApprovalModal from './ApprovalModal';
import TimesheetPayRatesModal from './TimesheetPayRatesModal';

interface TimesheetListProps {
    timesheets: TTimesheet[];
    onApprove: (timesheetIds: number[]) => Promise<void>;
    onReject: (timesheetId: number, reason: string) => Promise<void>;
    skipApprovalModal?: boolean;
}

const TimesheetList: React.FC<TimesheetListProps> = ({ timesheets = [], onApprove, onReject, skipApprovalModal = false }) => {
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [rejectingTimesheet, setRejectingTimesheet] = useState<TTimesheet | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [approvingTimesheetIds, setApprovingTimesheetIds] = useState<number[] | null>(null);
    const [payRateTimesheet, setPayRateTimesheet] = useState<TTimesheet | null>(null);

    // Update select all checkbox when selection changes
    useEffect(() => {
        if (timesheets && timesheets.length > 0) {
            setSelectAll(selectedIds.size === timesheets.length);
        } else {
            setSelectAll(false);
        }
    }, [selectedIds, timesheets]);

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set((timesheets || []).map(ts => ts.id)));
        }
    };

    const handleSelectTimesheet = (timesheetId: number) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(timesheetId)) {
            newSelected.delete(timesheetId);
        } else {
            newSelected.add(timesheetId);
        }
        setSelectedIds(newSelected);
    };

    const handleBulkApprove = async() => {
        if (selectedIds.size === 0) return;
        
        const ids = Array.from(selectedIds);
        
        if (skipApprovalModal) {
            // Directly approve without showing modal
            setIsSubmitting(true);
            try {
                await onApprove(ids);
                setSelectedIds(new Set());
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setApprovingTimesheetIds(ids);
        }
    };

    const handleApproveSingle = async(timesheetId: number) => {
        if (skipApprovalModal) {
            // Directly approve without showing modal
            setIsSubmitting(true);
            try {
                await onApprove([timesheetId]);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setApprovingTimesheetIds([timesheetId]);
        }
    };

    const handleApprovalSubmit = async() => {
        if (!approvingTimesheetIds || approvingTimesheetIds.length === 0) return;

        setIsSubmitting(true);
        try {
            await onApprove(approvingTimesheetIds);
            setApprovingTimesheetIds(null);
            // Clear selection if bulk approve
            if (approvingTimesheetIds.length > 1) {
                setSelectedIds(new Set());
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRejectClick = (timesheet: TTimesheet) => {
        setRejectingTimesheet(timesheet);
    };

    const handleRejectSubmit = async(reason: string) => {
        if (!rejectingTimesheet) return;

        setIsSubmitting(true);
        try {
            await onReject(rejectingTimesheet.id, reason);
            setRejectingTimesheet(null);
            // Remove from selection if it was selected
            const newSelected = new Set(selectedIds);
            newSelected.delete(rejectingTimesheet.id);
            setSelectedIds(newSelected);
        } finally {
            setIsSubmitting(false);
        }
    };

    const calculateTotal = (timesheet: TTimesheet): number => {
        if (!timesheet.pay_rates || timesheet.pay_rates.length === 0) {
            return 0;
        }
        return timesheet.pay_rates.reduce((total, rate) => {
            const duration = moment.duration(rate.duration);
            const hours = duration.asHours();
            const ratePerHour = parseFloat(rate.client_pay_per_hour);
            return total + (hours * ratePerHour);
        }, 0);
    };

    const calculateTotalHours = (timesheet: TTimesheet): number => {
        if (!timesheet.pay_rates || timesheet.pay_rates.length === 0) {
            return 0;
        }
        return timesheet.pay_rates.reduce((total, rate) => {
            const duration = moment.duration(rate.duration);
            return total + duration.asHours();
        }, 0);
    };

    if (!timesheets || timesheets.length === 0) {
        return (
            <div className="no-timesheets">
                <h2>All Caught Up!</h2>
                <p>There are no timesheets pending your approval at this time.</p>
            </div>
        );
    }

    return (
        <div className="timesheet-list">
            <table className="timesheet-table">
                <thead>
                    <tr>
                        <th className="timesheet-table__checkbox-col">
                            <label className="checkbox-container">
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    aria-label="Select all timesheets"
                                />
                                <span className="checkmark" />
                            </label>
                        </th>
                        <th>Worker Name</th>
                        <th>Timesheet Number</th>
                        <th>Week Ending</th>
                        <th>Total Hours</th>
                        <th>Total Charge (Net)</th>
                        <th className="timesheet-table__actions-col">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {(timesheets || []).map((timesheet, index) => {
                        const isSelected = selectedIds.has(timesheet.id);
                        const totalHours = calculateTotalHours(timesheet);
                        const totalAmount = calculateTotal(timesheet);
                        const weekEnding = moment(timesheet.timesheet_date).endOf('isoWeek').format('DD/MM/YYYY');

                        return (
                            <tr
                                key={timesheet.id}
                                className={index % 2 === 0 ? '-even' : '-odd'}
                            >
                                <td className="timesheet-table__checkbox-col">
                                    <label className="checkbox-container">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleSelectTimesheet(timesheet.id)}
                                            aria-label={`Select timesheet ${timesheet.timesheet_number}`}
                                        />
                                        <span className="checkmark" />
                                    </label>
                                </td>
                                <td>
                                    <span className="timesheet-table__worker-name">
                                        {timesheet.freelancer_name || `ID: ${timesheet.freelancer}`}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="timesheet-table__link-button"
                                        onClick={() => setPayRateTimesheet(timesheet)}
                                    >
                                        {timesheet.timesheet_number}
                                    </button>
                                </td>
                                <td>{weekEnding}</td>
                                <td>{totalHours.toFixed(0)}</td>
                                <td>£{totalAmount.toFixed(2)}</td>
                                <td className="timesheet-table__actions-col">
                                    <div className="timesheet-table__actions">
                                        <button
                                            type="button"
                                            className="table-action-button table-action-button-compact"
                                            onClick={() => handleApproveSingle(timesheet.id)}
                                            disabled={isSubmitting}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            type="button"
                                            className="table-action-button table-action-button-compact table-action-button-danger"
                                            onClick={() => handleRejectClick(timesheet)}
                                            disabled={isSubmitting}
                                        >
                                            Reject
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className="timesheet-list__footer">
                <button
                    type="button"
                    className="table-action-button"
                    onClick={handleBulkApprove}
                    disabled={selectedIds.size === 0 || isSubmitting}
                >
                    {isSubmitting ? 'Approving...' : 'Approve selected Timesheets'}
                </button>
            </div>

            {rejectingTimesheet && (
                <RejectionModal
                    timesheet={rejectingTimesheet}
                    onClose={() => setRejectingTimesheet(null)}
                    onSubmit={handleRejectSubmit}
                    isSubmitting={isSubmitting}
                />
            )}

            {approvingTimesheetIds && !skipApprovalModal && (
                <ApprovalModal
                    timesheetCount={approvingTimesheetIds.length}
                    onClose={() => setApprovingTimesheetIds(null)}
                    onSubmit={handleApprovalSubmit}
                    isSubmitting={isSubmitting}
                />
            )}

            {payRateTimesheet && (
                <TimesheetPayRatesModal
                    timesheet={payRateTimesheet}
                    onClose={() => setPayRateTimesheet(null)}
                />
            )}
        </div>
    );
};

export default TimesheetList;
